import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import { Button } from "components/Button"
import FatButton from "components/Button/FatButton/FatButton"
import { Buttons, Card } from "components/Card/Card"
import { FormPage } from "components/Form/Form"
import { Input } from "components/Input"
import { openNotification } from "components/Message/Message"
import { Keyboard, KeyboardReset } from "hocs/withKeyboard"
import PreviousButton from "components/Previous/PreviousButton"
import { Steps } from "components/Steps"
import { useStepper } from "components/Steps/hooks"
import actionsSurvey from "containers/Survey/services/actions"
import actionsUpload from "containers/Upload/services/actions"
import { PATH } from "containers/Survey/models"
import {
  CANCEL_PROFESSIONAL_TO_INSERT,
  CARERS_CLEAR_MESSAGE,
  GET_IDENTITY_FROM_RPPS_REQUEST,
} from "containers/InsertCarer/services/constants"
import { SURVEY_PAGE } from "core/constants"
import { cdn } from "core/cdn"
import { useLanguage } from "locales"
import { InsertCarerStore, AdminStore, ClientStore } from "types/store"
import {
  adminBillingRequest,
  adminOwnerInfoReset,
  adminOwnerInfoUpdateRequest,
} from "containers/Admin/services/actions"
import MssEmailEntry, { MSS_EMAIL_PATTERN } from "containers/PharmacySettings/MssEmailEntry"
import { knownMssEmail, useDirectoryMss } from "containers/PharmacySettings/useDirectoryMss"
import { pharmacistRppsLengthValidator } from "lib/form"

// A new owner then gets the MSS address entry of the owner info screen: the address the RPPS
// directory knows to confirm, or an empty field
type Step = "rpps_form" | "rpps_confirm" | "finess" | "mss"

const RPPS_LENGTH = 11
const FINESS_LENGTH = 9

const PharmacistInfo: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { languages } = useLanguage()
  const { current, insideStep, totalInsideSteps, totalSteps } = useStepper()

  const [step, setStep] = useState<Step>("rpps_form")
  const saveStarted = useRef(false)
  // Only an MSS save started on this screen may end the step
  const mssSaveStarted = useRef(false)
  const finessKeyboard = useRef<Keyboard>(null)
  const admin = useSelector(({ client }: { client: ClientStore }) => client.admin)
  // Prefilled with the kiosk's FINESS: a new owner usually keeps the pharmacy's
  const [finess, setFiness] = useState(admin?.finess ?? "")
  const [finessMessage, setFinessMessage] = useState<{ text: string; type: "error" | "success" } | null>(null)

  const professionalToInsert = useSelector(
    ({ carer }: { carer: Partial<InsertCarerStore> }) => carer.professionalToInsert,
  )
  const carerLoading = useSelector(
    ({ carer }: { carer: Partial<InsertCarerStore> }) => (carer as any).loading,
  )
  const carerMessage = useSelector(
    ({ carer }: { carer: Partial<InsertCarerStore> }) => (carer as any).message,
  )
  const adminLoading = useSelector(
    ({ admin }: { admin: Partial<AdminStore> }) => admin.loading,
  )
  const adminMessage = useSelector(
    ({ admin }: { admin: Partial<AdminStore> }) => admin.message,
  )
  const ownerInfo = useSelector(({ admin }: { admin: Partial<AdminStore> }) => admin.ownerInfo)
  const directory = useDirectoryMss(admin, step === "mss")
  const mssAddress = knownMssEmail(admin, directory)
  const mssLoading = !!ownerInfo?.loading

  useEffect(() => {
    dispatch({ type: CARERS_CLEAR_MESSAGE })
    return () => {
      dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT })
      dispatch(adminOwnerInfoReset())
    }
  }, [])

  useEffect(() => {
    if (professionalToInsert && step === "rpps_form") {
      setStep("rpps_confirm")
    }
  }, [professionalToInsert])

  const finishStep = () => {
    if (!professionalToInsert?.rpps) return
    dispatch(
      actionsSurvey.setStepReplies({
        id: PATH.tle_pharmacist,
        replies: [
          {
            title: PATH.tle_pharmacist,
            value: {
              rpps: professionalToInsert.rpps,
              finess: admin?.finess || finess.trim(),
              name: [professionalToInsert.firstname, professionalToInsert.lastname]
                .filter(Boolean)
                .join(" "),
            },
          },
        ],
      }),
    )
    dispatch(actionsUpload.reset())
    localStorage.setItem("tle_session_start", new Date().toISOString())
    dispatch(actionsSurvey.next())
    navigate(SURVEY_PAGE)
  }

  useEffect(() => {
    if (!saveStarted.current) return
    if (adminMessage?.type === "error") saveStarted.current = false
    if (step !== "finess" || adminMessage?.type !== "success" || !professionalToInsert?.rpps) return
    saveStarted.current = false
    openNotification({ type: "success", text: languages.ownerInfoSaved })
    // A new owner starts without MSS choice: ask the address like the owner info screen does.
    // An owner whose choice was kept (same RPPS) is not asked again
    if (admin?.mss_opt_in === true || admin?.mss_opt_in === false) {
      finishStep()
      return
    }
    dispatch(adminOwnerInfoReset())
    setStep("mss")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMessage, step, professionalToInsert, dispatch, navigate])

  useEffect(() => {
    if (!mssSaveStarted.current) return
    if (ownerInfo?.status === "error") mssSaveStarted.current = false
    if (ownerInfo?.status !== "success") return
    mssSaveStarted.current = false
    openNotification({ type: "success", text: languages.ownerInfoSaved })
    finishStep()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerInfo?.status])

  const handleFinessSubmit = () => {
    if (adminLoading || !professionalToInsert?.rpps) return
    const fiTrimmed = finess.trim()
    if (!/^\d{9}$/.test(fiTrimmed)) {
      setFinessMessage({ text: languages.pharmacistFinessLengthError, type: "error" })
      return
    }
    setFinessMessage(null)
    saveStarted.current = true
    dispatch(
      adminBillingRequest({
        rpps: professionalToInsert.rpps,
        finess: fiTrimmed,
        owner_firstname: professionalToInsert.firstname || "",
        owner_lastname: professionalToInsert.lastname || "",
      }),
    )
  }

  const leaveMss = () => {
    // Keep the pending state while a save is running, so nothing re-enables meanwhile
    if (!mssLoading) dispatch(adminOwnerInfoReset())
    setStep("finess")
  }

  const handlePrevious = () => {
    if (step === "mss") {
      leaveMss()
      return
    }
    if (step === "finess") {
      setStep("rpps_confirm")
      return
    }
    if (step === "rpps_confirm") {
      dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT })
      setStep("rpps_form")
      return
    }
    dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT })
    dispatch(actionsSurvey.previous())
    navigate(SURVEY_PAGE)
  }

  const rppsInputs = [
    { name: "rpps", required: true, label: "", rules: [pharmacistRppsLengthValidator], maxLength: RPPS_LENGTH },
  ]

  return (
    <>
      <Steps
        current={current}
        insideStep={insideStep}
        totalInsideSteps={totalInsideSteps}
        totalSteps={totalSteps}
      />
      {/* The MSS entry draws its own back button */}
      {(step !== "mss" || directory.loading) && <PreviousButton onClick={handlePrevious} />}

      {step === "mss" && directory.loading && <Card />}

      {step === "mss" && !directory.loading && (
        <MssEmailEntry
          layout="page"
          initialValue={mssAddress ?? ""}
          recovered={!admin?.mss_email && !!directory.email}
          confirmUnchanged
          loading={mssLoading}
          saveError={ownerInfo?.status === "error" ? ownerInfo.message : undefined}
          onBack={leaveMss}
          onConfirm={(email) => {
            mssSaveStarted.current = true
            dispatch(adminOwnerInfoUpdateRequest({ mss_email: email }))
          }}
          onSkip={(optOut) => {
            // Skipping a shown address is a refusal, stored with that address like on the owner
            // info screen (unless the API would refuse its domain); without any address nothing is stored
            if (!optOut) {
              finishStep()
              return
            }
            mssSaveStarted.current = true
            const addressToStore =
              mssAddress && !admin?.mss_email && MSS_EMAIL_PATTERN.test(mssAddress) ? mssAddress : undefined
            dispatch(
              adminOwnerInfoUpdateRequest({
                mss_opt_in: false,
                ...(addressToStore ? { mss_email: addressToStore } : {}),
              }),
            )
          }}
        />
      )}

      {step === "rpps_form" && (
        <Card message={carerMessage}>
          <FormPage
            title={
              <>
                <span style={{ fontWeight: 400 }}>{languages.pharmacistEnterRppsPrefix}{" "}</span>
                <strong>{languages.pharmacistTitulaireLabel}</strong>
              </>
            }
            inputs={rppsInputs}
            acceptText={languages.next}
            loading={carerLoading}
            onFinish={({ rpps }: { rpps: string }) =>
              dispatch({ type: GET_IDENTITY_FROM_RPPS_REQUEST, payload: { rpps } })
            }
            onCancel={handlePrevious}
          />
        </Card>
      )}

      {step === "rpps_confirm" && (
        <Card
          title={languages.pharmacistInfoTitle}
          style={{ height: "70vh", justifyContent: "center" }}
          message={carerMessage}
        >
          <div className="flex-column flex-center">
            <FatButton disabled>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "8px 0" }}>
                <img src={cdn("/images/ps_picto.svg")} alt="" />
                <b>
                  {professionalToInsert?.firstname} {professionalToInsert?.lastname}
                </b>
                <i>{professionalToInsert?.profession ?? languages.healthPro}</i>
                {professionalToInsert?.rpps && (
                  <span style={{ fontSize: 14, color: "#666" }}>
                    RPPS : {professionalToInsert.rpps}
                  </span>
                )}
              </div>
            </FatButton>
            <Buttons>
              <Button wide="long" onClick={handlePrevious}>
                {languages.cancel}
              </Button>
              <Button wide="long" type="primary" onClick={() => setStep("finess")}>
                {languages.pharmacistInfoContinue}
              </Button>
            </Buttons>
          </div>
        </Card>
      )}

      {step === "finess" && (
        <Card message={finessMessage ?? adminMessage}>
          <div style={{ fontSize: 65, fontWeight: 700, color: "#211452", lineHeight: "95px", textAlign: "center" }}>
            <strong>{languages.pharmacistFinessHeadingBold}</strong>{" "}
            <span style={{ fontWeight: 400 }}>{languages.pharmacistFinessHeadingSuffix}</span>
          </div>
          <Keyboard
            ref={finessKeyboard}
            options={{ type: "finess", inputName: "finess" }}
            value={finess}
            onChange={(val: string) => {
              // Same cap as the owner info screen: the keyboard stores the full value right after
              // this callback, so realign its own copy or backspace erases an invisible digit
              const capped = val.slice(0, FINESS_LENGTH)
              if (capped !== val) {
                queueMicrotask(() => finessKeyboard.current?.onUserKeyboardChange({ finess: capped }))
              }
              setFiness(capped)
              setFinessMessage(null)
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 60, marginBottom: 8 }}>
              <div style={{ fontSize: 38, color: "#211452" }}>
                {languages.pharmacistFinessInputPrefix}{" "}
                <strong>{languages.pharmacyLabel}</strong>
              </div>
              <div style={{ position: "relative" }}>
                <Input
                  placeholder={languages.pharmacistFinessPlaceholder}
                  value={finess}
                  name="finess"
                  maxLength={FINESS_LENGTH}
                />
                {finess ? (
                  <KeyboardReset
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setFiness("")
                      setFinessMessage(null)
                    }}
                  />
                ) : null}
              </div>
            </div>
          </Keyboard>

          <Buttons>
            <Button
              type="primary"
              onClick={handleFinessSubmit}
              disabled={!finess.trim() || adminLoading}
            >
              {languages.pharmacistInfoContinue}
            </Button>
          </Buttons>
        </Card>
      )}
    </>
  )
}

export default PharmacistInfo
